/**
 * Presentational view for a collapsible section block inside the all-pipelines
 * drawer (SE-5489) — Personal / Team / System. Renders title + count + chevron
 * header + rows, mirroring the Scouting `SeeAllListsSectionView`.
 *
 * Rows are hidden when collapsed; the header + count stay visible. Each row is
 * a {@link PipelineDrawerRow} that lazily resolves its own card count.
 * @module features/player-kanban/components/molecules/SeeAllPipelinesDrawer/SeeAllPipelinesSection/SeeAllPipelinesSectionView
 */

import { ChevronDownIcon, ChevronRightIcon } from "lucide-react-native";
import { memo } from "react";

import PipelineDrawerRow from "../PipelineDrawerRow";

import type { PipelineDrawerRow as PipelineDrawerRowData } from "@/features/player-kanban/hooks/useSeeAllPipelinesData";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for the section view. */
export interface SeeAllPipelinesSectionViewProps {
  readonly title: string;
  readonly rows: readonly PipelineDrawerRowData[];
  readonly isCollapsed: boolean;
  readonly isDrawerOpen: boolean;
  readonly onToggle: () => void;
  readonly onSelectRow: (id: string) => void;
  readonly sectionTestId: string;
  readonly chevronTestId: string;
}

/**
 * Section view component.
 * @param root0 - Component props
 * @param root0.title - Section title text.
 * @param root0.rows - Rows to render.
 * @param root0.isCollapsed - Whether the section is collapsed.
 * @param root0.isDrawerOpen - Whether the drawer is open (gates row count queries).
 * @param root0.onToggle - Chevron tap handler.
 * @param root0.onSelectRow - Row tap handler.
 * @param root0.sectionTestId - Section root testID.
 * @param root0.chevronTestId - Chevron testID.
 */
const SeeAllPipelinesSectionView = ({
  title,
  rows,
  isCollapsed,
  isDrawerOpen,
  onToggle,
  onSelectRow,
  sectionTestId,
  chevronTestId,
}: SeeAllPipelinesSectionViewProps) => (
  /* The section header owns the horizontal inset; the ROWS do not, so a row's
     hover fill runs the full width of the panel instead of stopping short of
     the edges. Rows carry their own px-6 to keep their text aligned with the
     header above them. */
  <Box testID={sectionTestId} className="flex-col">
    <Pressable
      testID={chevronTestId}
      onPress={onToggle}
      className="flex-row items-center gap-3 px-6 py-4"
    >
      <Text variant="title-sm-medium" className="flex-1 text-content-muted">
        {title} ({rows.length})
      </Text>
      <Icon
        as={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        size="sm"
        className="text-content-muted"
      />
    </Pressable>
    {isCollapsed
      ? null
      : rows.map(row => (
          <PipelineDrawerRow
            key={row.id}
            row={row}
            isDrawerOpen={isDrawerOpen}
            onSelect={onSelectRow}
          />
        ))}
  </Box>
);

SeeAllPipelinesSectionView.displayName = "SeeAllPipelinesSectionView";

export default memo(SeeAllPipelinesSectionView);
